import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

const DOMAIN_ACCENTS = {
  healthcare: "#10B981",
  technology: "#06B6D4",
  arts_culture: "#F59E0B",
  law_governance: "#4F46E5",
  commerce_economy: "#64748B",
};

export default function IndexCategory() {
  const { type } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/indices/${type}`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [type]);

  if (error)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 text-red-600 font-body" data-testid="index-error">{error}</p>
      </div>
    );

  if (!data)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
          Loading…
        </p>
      </div>
    );

  const items = data.items || [];

  return (
    <div className="App" data-testid={`index-category-${type}`}>
      <Navbar />
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <Link
            to="/indices"
            className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)] hover:text-[var(--es-blue)]"
            data-testid="index-back-link"
          >
            <ArrowLeft size={14} strokeWidth={1.6} /> All indices
          </Link>
          <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)] mt-8">
            The Index of
          </p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-[var(--es-ink)] tracking-tight mt-2 leading-[1.02]">
            {data.label}
          </h1>
          <p className="font-body text-[var(--es-ink-soft)] mt-4 max-w-2xl text-lg">{data.blurb}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        <div className="border-t border-[var(--es-line)]">
          {items.map((it, i) => (
            <Link
              key={it.slug}
              to={`/indices/${type}/${it.slug}`}
              className="grid grid-cols-12 gap-3 md:gap-6 items-center border-b border-[var(--es-line)] py-5 px-2 hover:bg-[var(--es-bg-soft)] transition-colors group"
              data-testid={`index-row-${it.slug}`}
            >
              <span className="col-span-1 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="col-span-12 md:col-span-4 order-2 md:order-none">
                <p className="font-display text-2xl text-[var(--es-ink)]">{it.name}</p>
                <p className="font-body text-sm text-[var(--es-ink-soft)] line-clamp-1">
                  {it.summary}
                </p>
              </div>
              <div className="col-span-7 md:col-span-5 hidden md:flex items-center gap-1.5">
                {it.domain_scores.map((d) => (
                  <span
                    key={d.key}
                    title={`${d.label}: ${d.score}`}
                    className="flex-1 h-8 relative"
                    style={{ background: DOMAIN_ACCENTS[d.key] + "1A" }}
                  >
                    <span
                      className="absolute left-0 bottom-0 right-0"
                      style={{ height: `${(d.score / 10) * 100}%`, background: DOMAIN_ACCENTS[d.key] }}
                    />
                  </span>
                ))}
              </div>
              <span className="col-span-3 md:col-span-1 font-display text-3xl text-[var(--es-blue)] text-right order-1 md:order-none">
                {it.overall}
              </span>
              <span className="col-span-9 md:col-span-1 font-ui text-[10px] uppercase tracking-widest text-[var(--es-ink-soft)] text-right group-hover:text-[var(--es-blue)] transition-colors">
                {it.tier?.label}
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
